import { ReactElement } from "react";
import classNames from "classnames";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import styles from "./RadioButton.module.less";

export type RadioButtonColor =
	| "info"
	| "success"
	| "warning"
	| "danger"
	| "primary";

export type RadioButtonProps = {
	group?: string;
	value?: boolean;
	onChange(value: boolean): void;
	isDisabled?: boolean;
	text?: string;
	color?: RadioButtonColor;
	ellipseText?: boolean;
	trackingId?: string;
};

export const RadioButton = (props: RadioButtonProps): ReactElement => {
	const {
		group,
		value,
		isDisabled,
		text,
		color,
		ellipseText = true,
		onChange,
		trackingId
	} = props;

	const colorStyle: string | undefined = color
		? radioButtonColorToStylesMap[color]
		: undefined;

	return (
		<label
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{ [styles.root_disabled]: isDisabled }
			)}
		>
			<input
				{...setTrackingId(trackingId, "__input")}
				type="radio"
				checked={value}
				disabled={isDisabled}
				name={group}
				onChange={(args) => onChange(args.target.checked)}
			/>
			<span
				{...setTrackingId(trackingId, "__glyph")}
				aria-hidden
				className={classNames(
					styles.glyph,
					colorStyle,
					{
						[styles.glyph_colored]: isNotNil(color)
					}
				)}
			/>
			{ text && (
				<span
					{...setTrackingId(trackingId, "__text")}
					className={classNames(
						styles.text,
						{
							[styles.text_ellipsed]: ellipseText,
							[styles.text_primary]: value && color === "primary"
						}
					)}
				>
					{text}
				</span>
			)}
		</label>
	);
};
RadioButton.displayName = "RadioButton";

const radioButtonColorToStylesMap: { [key in RadioButtonColor]: string } = {
	success: styles.glyph_success,
	info: styles.glyph_info,
	danger: styles.glyph_error,
	warning: styles.glyph_warning,
	primary: styles.glyph_primary
};
